/**
 * §20 — Gym/Estudio. Vistas que cuelgan del espacio "Wellness" (clave interna del
 * módulo: 'gym'): Miembros (alta + cobro), Check-in (validación), Planes, Visitas.
 * Reusa el cliente `api` + contactos del kernel. Deadline-driven: happy path.
 */
import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { DatePeriodFilter, periodToQuery, periodLabel, defaultPeriod, type PeriodValue } from '../components/DatePeriodFilter';
import { ContactActions } from '../boutique/BoutiqueViews';

const fmtMoney = (v: any) => {
  if (v == null) return '—';
  try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(v)); }
  catch { return `$${Number(v).toFixed(2)}`; }
};
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('es-MX') : '—');

const PLAN_TYPE_LABEL: Record<string, string> = {
  UNLIMITED_MONTH: 'Ilimitado mensual',
  PACKAGE_N: 'Paquete de clases',
  SINGLE: 'Clase individual',
};

const CHECKIN_META: Record<string, { label: string; ok: boolean }> = {
  OK: { label: 'Acceso permitido', ok: true },
  BLOCKED_EXPIRED: { label: 'Membresía vencida', ok: false },
  BLOCKED_UNPAID: { label: 'Pago pendiente', ok: false },
  BLOCKED_NO_CLASSES: { label: 'Sin clases disponibles', ok: false },
};

type Member = {
  id: string; contactId: string; planId: string;
  startDate: string; endDate: string; classesRemaining: number | null;
  paymentStatus: 'PAID' | 'PENDING'; priceCharged: number;
  planName: string | null; planType: string | null;
  memberName: string; memberEmail: string | null; memberPhone: string | null;
  isExpired: boolean;
  /** Check-ins OK (asistencia real) en el período seleccionado. */
  visits?: number;
};
type CheckInRow = {
  id: string; result: string; checkedAt: string;
  memberSubscriptionId: string; accessTypeId: string | null;
};
type Plan = {
  id: string; name: string; planType: string; price: number;
  /** V220 — IVA del plan: con IVA aparte se cobra price × (1 + taxRate/100). */
  taxRate?: number; priceIncludesTax?: boolean;
  validityDays: number; classesIncluded: number | null; isActive: boolean;
  /** V71 — tope de reservas/día en el canal de booking. null = sin tope; SINGLE = 1 fijo. */
  maxBookingsPerDay: number | null;
};
type AccessType = { id: string; name: string; isActive: boolean };

/** V220 — lo que se COBRA por un plan (mismo cálculo que el backend, membershipCharge). */
export function planChargeTotal(p: { price: number | string; taxRate?: number | string; priceIncludesTax?: boolean }): number {
  const price = Number(p.price) || 0; const rate = (Number(p.taxRate ?? 16) || 0) / 100;
  const total = p.priceIncludesTax !== false || rate <= 0 ? price : price * (1 + rate);
  return Math.round((total + Number.EPSILON) * 100) / 100;
}

/** V220 — aviso del formulario cuando el precio va SIN IVA: cuánto se cobrará de verdad. */
export function PlanTaxHint({ price, includesTax, taxRate = 16 }: { price: string | number; includesTax: boolean; taxRate?: number }) {
  if (includesTax || !(Number(price) > 0)) return null;
  return (
    <p className="text-[12px] text-amber-700">
      Se cobrará {fmtMoney(planChargeTotal({ price, taxRate, priceIncludesTax: false }))} con IVA ({fmtMoney(Number(price))} + {taxRate} %).
    </p>
  );
}

const card = 'rounded-2xl border border-slate-200 bg-white';
const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';
const inputCls = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm';

function statusOf(m: Member): { label: string; cls: string } {
  if (m.isExpired) return { label: 'Vencida', cls: 'bg-rose-100 text-rose-700' };
  if (m.paymentStatus !== 'PAID') return { label: 'Pago pendiente', cls: 'bg-amber-100 text-amber-700' };
  if (m.classesRemaining === 0) return { label: 'Sin clases', cls: 'bg-amber-100 text-amber-700' };
  return { label: 'Vigente', cls: 'bg-emerald-100 text-emerald-700' };
}

function classesLabel(m: Member | Plan): string {
  const n = 'classesRemaining' in m ? m.classesRemaining : m.classesIncluded;
  return n == null ? 'Ilimitado' : `${n}`;
}

// ---------------------------------------------------------------------------
// Miembros
// ---------------------------------------------------------------------------
export function GymMembersView() {
  const [members, setMembers] = useState<Member[]>([]);
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [renewing, setRenewing] = useState<Member | null>(null);
  const [changing, setChanging] = useState<Member | null>(null);
  const [ficha, setFicha] = useState<Member | null>(null);

  const load = () => api<Member[]>(`/gym/members?${new URLSearchParams(periodToQuery(period)).toString()}`)
    .then(setMembers)
    .catch((e: any) => setErr(e?.body?.message || e?.message || 'No se pudieron cargar los miembros'));
  useEffect(() => { load(); }, [period]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">{members.length} miembro(s) · visitas de {periodLabel(period)}</p>
        <div className="flex items-center gap-3">
          <DatePeriodFilter value={period} onChange={setPeriod} />
          <button className={btnPrimary} onClick={() => setShowNew(true)}>+ Nuevo miembro</button>
        </div>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}

      <div className={card}>
        {members.length === 0
          ? <p className="px-5 py-6 text-sm text-slate-400">Sin miembros. Da de alta el primero.</p>
          : (
            <div className="divide-y divide-slate-100">
              {members.map((m) => {
                const st = statusOf(m);
                return (
                  <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                    <button className="min-w-0 text-left hover:opacity-70" onClick={() => setFicha(m)} title="Ver ficha del miembro">
                      <p className="font-medium text-slate-800">{m.memberName || 'Miembro'}</p>
                      <p className="text-[12px] text-slate-400">
                        {m.planName ?? PLAN_TYPE_LABEL[m.planType ?? ''] ?? '—'} · vence {fmtDate(m.endDate)} · clases: {classesLabel(m)}
                      </p>
                    </button>
                    <span className="flex items-center gap-2">
                      <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-slate-600" title={`Visitas en ${periodLabel(period)}`}>
                        {m.visits ?? 0} visita{(m.visits ?? 0) === 1 ? '' : 's'}
                      </span>
                      <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                      <button className={btnGhost} onClick={() => setRenewing(m)}>Renovar</button>
                      <button className={btnGhost} onClick={() => setChanging(m)}>Cambiar plan</button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {ficha && <MemberFichaModal member={ficha} periodLabelText={periodLabel(period)} onClose={() => setFicha(null)} />}
      {showNew && <NewMemberModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {renewing && <RenewModal member={renewing} onClose={() => setRenewing(null)} onSaved={() => { setRenewing(null); load(); }} />}
      {changing && <ChangePlanModal member={changing} onClose={() => setChanging(null)} onSaved={() => { setChanging(null); load(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ficha del miembro — visitas del período + historial de check-ins (read-only).
// Reusa /gym/check-ins?subscriptionId= (ya existente). Operativo: sin financials.
// ---------------------------------------------------------------------------
function MemberFichaModal({ member, periodLabelText, onClose }: { member: Member; periodLabelText: string; onClose: () => void }) {
  const [history, setHistory] = useState<CheckInRow[]>([]);
  const [loading, setLoading] = useState(true);
  const st = statusOf(member);

  useEffect(() => {
    let cancelled = false;
    api<CheckInRow[]>(`/gym/check-ins?subscriptionId=${member.id}`)
      .then((r) => { if (!cancelled) setHistory(r); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [member.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className={`${card} max-h-[90vh] w-full max-w-lg overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-lg text-slate-900">{member.memberName || 'Miembro'}</h3>
            <p className="text-[12px] text-slate-400">
              {member.planName ?? '—'} · vence {fmtDate(member.endDate)} · clases: {classesLabel(member)}
            </p>
          </div>
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className={`${card} px-4 py-3`}>
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Visitas · {periodLabelText}</p>
            <p className="font-serif text-2xl tabular-nums text-slate-900">{member.visits ?? 0}</p>
          </div>
          <div className={`${card} px-4 py-3`}>
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Contactar</p>
            <div className="mt-1">
              <ContactActions name={member.memberName} phone={member.memberPhone} email={member.memberEmail}
                message="te extrañamos en el estudio, ¿retomamos tu rutina?" subject="Te extrañamos" />
            </div>
          </div>
        </div>

        <h4 className="mt-5 mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Historial de check-ins</h4>
        {loading ? <p className="text-sm text-slate-400">Cargando…</p> : history.length === 0 ? (
          <p className="text-sm text-slate-400">Sin check-ins registrados.</p>
        ) : (
          <div className="max-h-56 divide-y divide-slate-100 overflow-y-auto">
            {history.map((h) => {
              const meta = CHECKIN_META[h.result] ?? { label: h.result, ok: false };
              return (
                <div key={h.id} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-600">{new Date(h.checkedAt).toLocaleString('es-MX')}</span>
                  <span className={`text-[12px] font-medium ${meta.ok ? 'text-emerald-700' : 'text-rose-600'}`}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function RenewModal({ member, onClose, onSaved }: { member: Member; onClose: () => void; onSaved: () => void }) {
  const [method, setMethod] = useState('CASH');
  const [charge, setCharge] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      await api(`/gym/members/${member.id}/renew`, { method: 'POST', body: { method, charge } });
      onSaved();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo renovar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className={`${card} max-h-[90vh] w-full max-w-md overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-lg text-slate-900">Renovar membresía</h3>
        <p className="mb-4 text-[12px] text-slate-400">
          {member.memberName} · {member.planName ?? '—'}. Extiende la vigencia y recarga clases (mismo plan). Vence hoy: {fmtDate(member.endDate)}.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-[12px] text-slate-500">Método de pago</label>
            <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="CASH">Efectivo</option>
              <option value="CARD">Tarjeta</option>
              <option value="TRANSFER">Transferencia</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={charge} onChange={(e) => setCharge(e.target.checked)} /> Registrar cobro
          </label>
        </div>
        {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>Cancelar</button>
          <button className={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Renovando…' : 'Renovar'}</button>
        </div>
      </div>
    </div>
  );
}

function ChangePlanModal({ member, onClose, onSaved }: { member: Member; onClose: () => void; onSaved: () => void }) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState('');
  const [method, setMethod] = useState('CASH');
  const [charge, setCharge] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<Plan[]>('/gym/plans').then((ps) => setPlans(ps.filter((p) => p.isActive))).catch(() => setPlans([]));
  }, []);

  const submit = async () => {
    if (!planId) { setErr('Elige el plan nuevo'); return; }
    setSaving(true); setErr(null);
    try {
      await api(`/gym/members/${member.id}/change-plan`, { method: 'POST', body: { planId, method, charge } });
      onSaved();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo cambiar el plan');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className={`${card} max-h-[90vh] w-full max-w-md overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-lg text-slate-900">Cambiar de plan</h3>
        <p className="mb-4 text-[12px] text-slate-400">{member.memberName} · actual: {member.planName ?? '—'}. El plan nuevo fija nueva vigencia y clases.</p>
        <div className="space-y-3">
          <div>
            <label className="text-[12px] text-slate-500">Plan nuevo</label>
            <select className={inputCls} value={planId} onChange={(e) => setPlanId(e.target.value)}>
              <option value="">— Elegir —</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · {fmtMoney(planChargeTotal(p))}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] text-slate-500">Método de pago</label>
            <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="CASH">Efectivo</option>
              <option value="CARD">Tarjeta</option>
              <option value="TRANSFER">Transferencia</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={charge} onChange={(e) => setCharge(e.target.checked)} /> Registrar cobro
          </label>
        </div>
        {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>Cancelar</button>
          <button className={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Guardando…' : 'Cambiar plan'}</button>
        </div>
      </div>
    </div>
  );
}

function NewMemberModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [contacts, setContacts] = useState<any[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [contactId, setContactId] = useState('');
  const [planId, setPlanId] = useState('');
  const [method, setMethod] = useState('CASH');
  const [charge, setCharge] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<any>('/contacts?pageSize=100').then((r) => setContacts(r?.data ?? r ?? [])).catch(() => setContacts([]));
    api<Plan[]>('/gym/plans').then((ps) => setPlans(ps.filter((p) => p.isActive))).catch(() => setPlans([]));
  }, []);

  const submit = async () => {
    if (!contactId || !planId) { setErr('Elige contacto y plan'); return; }
    setSaving(true); setErr(null);
    try {
      await api('/gym/members', { method: 'POST', body: { contactId, planId, method, charge } });
      onSaved();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo dar de alta');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      {/* #249 — flex-col + max-h-[90vh]: header y footer fijos, contenido scrolleable
          para no dejar "Dar de alta" fuera de pantalla en tablets. */}
      <div className={`${card} flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden`} onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 p-6 pb-3">
          <h3 className="font-serif text-lg text-slate-900">Nuevo miembro</h3>
          <p className="text-[12px] text-slate-400">El miembro es un contacto. Al alta se registra el cobro.</p>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6">
          <div>
            <label className="text-[12px] text-slate-500">Contacto (miembro)</label>
            <select className={inputCls} value={contactId} onChange={(e) => setContactId(e.target.value)}>
              <option value="">— Elegir —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{`${c.firstName} ${c.lastName ?? ''}`.trim()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[12px] text-slate-500">Plan</label>
            <select className={inputCls} value={planId} onChange={(e) => setPlanId(e.target.value)}>
              <option value="">— Elegir —</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {fmtMoney(planChargeTotal(p))}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-[12px] text-slate-500">Método de pago</label>
              <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="CASH">Efectivo</option>
                <option value="CARD">Tarjeta</option>
                <option value="TRANSFER">Transferencia</option>
              </select>
            </div>
            <label className="mt-6 flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={charge} onChange={(e) => setCharge(e.target.checked)} />
              Registrar cobro
            </label>
          </div>
          {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
        </div>
        <div className="shrink-0 flex justify-end gap-2 border-t border-slate-100 p-6 py-3">
          <button className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>Cancelar</button>
          <button className={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Guardando…' : 'Dar de alta'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Check-in
// ---------------------------------------------------------------------------
export function GymCheckInView() {
  const [members, setMembers] = useState<Member[]>([]);
  const [search, setSearch] = useState('');
  const [accessTypes, setAccessTypes] = useState<AccessType[]>([]);
  const [picking, setPicking] = useState<Member | null>(null); // miembro elegido → popover de tipo de acceso
  const [result, setResult] = useState<{ name: string; result: string; classesRemaining: number | null; endDate: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api<Member[]>(`/gym/members${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`)
    .then(setMembers)
    .catch((e: any) => setErr(e?.body?.message || e?.message || 'Error'));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api<AccessType[]>('/gym/access-types')
      .then((ts) => setAccessTypes(ts.filter((t) => t.isActive)))
      .catch(() => setAccessTypes([]));
  }, []);

  // Al dar click en "Check-in": si hay tipos de acceso, se abre el popover para
  // elegir uno (botones); si no hay ninguno, se registra el acceso directo.
  const startCheckIn = (m: Member) => {
    if (accessTypes.length > 0) setPicking(m);
    else doCheckIn(m);
  };

  const doCheckIn = async (m: Member, accessTypeId?: string) => {
    setErr(null); setPicking(null);
    try {
      const body: any = { subscriptionId: m.id };
      if (accessTypeId) body.accessTypeId = accessTypeId;
      const r = await api<any>('/gym/check-ins', { method: 'POST', body });
      setResult({ name: m.memberName, result: r.result, classesRemaining: r.classesRemaining, endDate: r.endDate });
      load();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo registrar el check-in');
    }
  };

  return (
    <div className="space-y-5">
      {result && (() => {
        const meta = CHECKIN_META[result.result] ?? { label: result.result, ok: false };
        return (
          <div className={`rounded-2xl border p-5 ${meta.ok ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
            <p className={`font-serif text-xl ${meta.ok ? 'text-emerald-800' : 'text-rose-800'}`}>
              {meta.ok ? '✓' : '✕'} {meta.label}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {result.name} · clases restantes: {result.classesRemaining == null ? 'Ilimitado' : result.classesRemaining} · vence {fmtDate(result.endDate)}
            </p>
          </div>
        );
      })()}

      <div className="flex gap-2">
        <input className={inputCls} placeholder="Buscar miembro por nombre o email…"
               value={search} onChange={(e) => setSearch(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
        <button className={btnPrimary} onClick={load}>Buscar</button>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}

      <div className={card}>
        {members.length === 0
          ? <p className="px-5 py-6 text-sm text-slate-400">Sin miembros.</p>
          : (
            <div className="divide-y divide-slate-100">
              {members.map((m) => {
                const st = statusOf(m);
                return (
                  <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{m.memberName || 'Miembro'}</p>
                      <p className="text-[12px] text-slate-400">{m.planName ?? '—'} · vence {fmtDate(m.endDate)} · clases: {classesLabel(m)}</p>
                    </div>
                    <span className="flex items-center gap-3">
                      <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                      <button className={btnPrimary} onClick={() => startCheckIn(m)}>Check-in</button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* §20.1 — al hacer check-in se elige el tipo de acceso con BOTONES (no dropdown).
          Los tipos vienen de /gym/access-types (Interno, Visita, TotalPass…). */}
      {picking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setPicking(null)}>
          <div className={`${card} max-h-[90vh] w-full max-w-md overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-slate-900">Tipo de acceso</h3>
            <p className="mb-4 text-[12px] text-slate-400">{picking.memberName} · elige cómo registra su acceso.</p>
            <div className="flex flex-wrap gap-2">
              {accessTypes.map((t) => (
                <button key={t.id} className={btnPrimary} onClick={() => doCheckIn(picking, t.id)}>{t.name}</button>
              ))}
            </div>
            <div className="mt-5 flex justify-end">
              <button className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={() => setPicking(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Planes
// ---------------------------------------------------------------------------
export function GymPlansView() {
  // Crear, editar, desactivar y reactivar planes: solo admin (el backend responde 403 a los demás).
  const isAdmin = useAuth()?.user?.role === 'SUPER_USER';
  const [plans, setPlans] = useState<Plan[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', planType: 'UNLIMITED_MONTH', price: '', validityDays: '30', classesIncluded: '', bookingsPerDay: '', includesTax: true });
  const [saving, setSaving] = useState(false);
  // V71 — edición inline del tope de reservas/día de un plan existente.
  const [capEdit, setCapEdit] = useState<{ id: string; value: string; noCap: boolean } | null>(null);
  // Editar plan (modal) y desactivar/reactivar.
  const [editing, setEditing] = useState<Plan | null>(null);
  const toggleActive = async (p: Plan) => {
    setErr(null);
    try {
      if (p.isActive) await api(`/gym/plans/${p.id}`, { method: 'DELETE' });
      else await api(`/gym/plans/${p.id}`, { method: 'PUT', body: { isActive: true } });
      load();
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo cambiar el plan'); }
  };

  const load = () => api<Plan[]>('/gym/plans').then(setPlans).catch((e: any) => setErr(e?.message || 'Error'));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.price) { setErr('Nombre y precio requeridos'); return; }
    setSaving(true); setErr(null);
    try {
      await api('/gym/plans', {
        method: 'POST',
        body: {
          name: form.name.trim(),
          planType: form.planType,
          price: Number(form.price),
          priceIncludesTax: form.includesTax, // V220: default IVA incluido
          validityDays: Number(form.validityDays) || 30,
          ...(form.planType !== 'UNLIMITED_MONTH' && form.classesIncluded
            ? { classesIncluded: Number(form.classesIncluded) }
            : {}),
          // V71 — vacío = default 1 (lo pone el backend); SINGLE siempre 1.
          ...(form.planType !== 'SINGLE' && form.bookingsPerDay !== ''
            ? { maxBookingsPerDay: Number(form.bookingsPerDay) }
            : {}),
        },
      });
      setForm({ name: '', planType: 'UNLIMITED_MONTH', price: '', validityDays: '30', classesIncluded: '', bookingsPerDay: '', includesTax: true });
      load();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo crear el plan');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      {!isAdmin && <p className="text-sm text-slate-500">Solo el administrador crea o edita planes.</p>}
      {isAdmin && <div className={`${card} p-5`}>
        <h3 className="mb-3 font-serif text-base text-slate-900">Nuevo plan</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <input className={inputCls} placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select className={inputCls} value={form.planType} onChange={(e) => setForm({ ...form, planType: e.target.value })}>
            <option value="UNLIMITED_MONTH">Ilimitado mensual</option>
            <option value="PACKAGE_N">Paquete de clases</option>
            <option value="SINGLE">Clase individual</option>
          </select>
          <input className={inputCls} type="number" placeholder="Precio" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <input className={inputCls} type="number" placeholder="Vigencia (días)" value={form.validityDays} onChange={(e) => setForm({ ...form, validityDays: e.target.value })} />
          <input className={inputCls} type="number" placeholder="Clases (vacío = ilimitado)"
                 disabled={form.planType === 'UNLIMITED_MONTH'}
                 value={form.classesIncluded} onChange={(e) => setForm({ ...form, classesIncluded: e.target.value })} />
          {/* V71 — tope de reservas/día en el canal de booking. SINGLE = 1 fijo. */}
          <input className={inputCls} type="number" min={0}
                 placeholder="Reservas/día (vacío = 1)"
                 title={form.planType === 'SINGLE' ? 'Los planes de visita tienen 1 reserva por día, fijo' : 'Tope diario en reservas en línea'}
                 disabled={form.planType === 'SINGLE'}
                 value={form.planType === 'SINGLE' ? '1' : form.bookingsPerDay}
                 onChange={(e) => setForm({ ...form, bookingsPerDay: e.target.value })} />
        </div>
        {/* V220 — IVA del precio: incluido por default; si va aparte, se ve lo que se cobrará. */}
        <div className="mt-3 space-y-1">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.includesTax} onChange={(e) => setForm({ ...form, includesTax: e.target.checked })} />
            El precio incluye IVA
          </label>
          <PlanTaxHint price={form.price} includesTax={form.includesTax} />
        </div>
        {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
        <div className="mt-4"><button className={btnPrimary} onClick={create} disabled={saving}>{saving ? 'Guardando…' : 'Crear plan'}</button></div>
      </div>}
      {!isAdmin && err && <p className="text-sm text-rose-600">{err}</p>}

      <div className={card}>
        {plans.length === 0
          ? <p className="px-5 py-6 text-sm text-slate-400">Sin planes.</p>
          : (
            <div className="divide-y divide-slate-100">
              {plans.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                  <div>
                    <p className="font-medium text-slate-800">{p.name} {!p.isActive && <span className="text-[11px] text-slate-400">(inactivo)</span>}</p>
                    <p className="text-[12px] text-slate-400">
                      {PLAN_TYPE_LABEL[p.planType]} · {p.validityDays} días · clases: {classesLabel(p)} ·
                      {' '}reservas/día: {p.maxBookingsPerDay == null ? 'sin tope' : p.maxBookingsPerDay}
                    </p>
                  </div>
                  <span className="flex items-center gap-2">
                    {/* V71 — editar el tope (SINGLE queda fijo en 1). */}
                    {isAdmin && p.planType !== 'SINGLE' && (
                      capEdit?.id === p.id ? (
                        <span className="flex items-center gap-1.5">
                          <input type="number" min={0} className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-[12px]"
                                 disabled={capEdit.noCap} value={capEdit.noCap ? '' : capEdit.value}
                                 onChange={(e) => setCapEdit({ ...capEdit, value: e.target.value })} />
                          <label className="flex items-center gap-1 text-[11px] text-slate-500">
                            <input type="checkbox" checked={capEdit.noCap}
                                   onChange={(e) => setCapEdit({ ...capEdit, noCap: e.target.checked })} />
                            sin tope
                          </label>
                          <button className={btnGhost} onClick={async () => {
                            try {
                              await api(`/gym/plans/${p.id}`, {
                                method: 'PUT',
                                body: { maxBookingsPerDay: capEdit.noCap ? null : Number(capEdit.value || 1) },
                              });
                              setCapEdit(null); load();
                            } catch (e: any) { setErr(e?.body?.message || 'No se pudo guardar el tope'); }
                          }}>Guardar</button>
                        </span>
                      ) : (
                        <button className={btnGhost}
                                onClick={() => setCapEdit({ id: p.id, value: `${p.maxBookingsPerDay ?? 1}`, noCap: p.maxBookingsPerDay == null })}>
                          Tope/día
                        </button>
                      )
                    )}
                    {isAdmin && <button className={btnGhost} onClick={() => setEditing(p)}>Editar</button>}
                    {isAdmin && <button className={btnGhost} onClick={() => toggleActive(p)}>{p.isActive ? 'Desactivar' : 'Reactivar'}</button>}
                    <span className="font-medium tabular-nums text-slate-700">
                      {p.priceIncludesTax === false
                        ? `${fmtMoney(p.price)} + IVA = ${fmtMoney(planChargeTotal(p))}`
                        : fmtMoney(p.price)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
      </div>

      {editing && <EditPlanModal plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {/* §20.1 — Tipos de acceso (catálogo editable). NO agregadores (TotalPass/Wellhub). */}
      <AccessTypesManager />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editar plan: nombre, precio, IVA, vigencia, clases y tope. El tipo no se cambia.
// ---------------------------------------------------------------------------
/** Cuerpo del PUT a partir del formulario (exportado para vitest). Solo manda clases si el plan es
 *  paquete; el tope de un SINGLE es fijo (1) y no se manda. */
export function editPlanBody(plan: Pick<Plan, 'planType'>, f: { name: string; price: string; includesTax: boolean; validityDays: string; classesIncluded: string; bookingsPerDay: string; noCap: boolean }) {
  return {
    name: f.name.trim(),
    price: Number(f.price),
    priceIncludesTax: f.includesTax,
    validityDays: Number(f.validityDays),
    ...(plan.planType === 'PACKAGE_N' ? { classesIncluded: Number(f.classesIncluded) } : {}),
    ...(plan.planType !== 'SINGLE' ? { maxBookingsPerDay: f.noCap ? null : Number(f.bookingsPerDay || 1) } : {}),
  };
}

export const EDIT_PLAN_NOTE = 'Los cambios aplican a altas y renovaciones nuevas; las suscripciones vigentes conservan lo contratado.';

export function EditPlanModal({ plan, onClose, onSaved }: { plan: Plan; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    name: plan.name, price: String(plan.price), includesTax: plan.priceIncludesTax !== false,
    validityDays: String(plan.validityDays), classesIncluded: plan.classesIncluded == null ? '' : String(plan.classesIncluded),
    bookingsPerDay: plan.maxBookingsPerDay == null ? '' : String(plan.maxBookingsPerDay), noCap: plan.maxBookingsPerDay == null,
  });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !(Number(f.price) > 0)) { setErr('Nombre y precio requeridos'); return; }
    if (!(Number(f.validityDays) >= 1)) { setErr('La vigencia es de al menos 1 día'); return; }
    if (plan.planType === 'PACKAGE_N' && !(Number(f.classesIncluded) >= 1)) { setErr('Un paquete incluye al menos 1 clase'); return; }
    setSaving(true); setErr(null);
    try {
      await api(`/gym/plans/${plan.id}`, { method: 'PUT', body: editPlanBody(plan, f) });
      onSaved();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo guardar el plan');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className={`${card} flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden`} onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 p-6 pb-3">
          <h3 className="font-serif text-lg text-slate-900">Editar plan</h3>
          <p className="text-[12px] text-slate-400">{PLAN_TYPE_LABEL[plan.planType] ?? plan.planType} · el tipo no se cambia (crea otro plan)</p>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6">
          <div><label className="text-[12px] text-slate-500">Nombre</label>
            <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="text-[12px] text-slate-500">Precio</label>
            <input className={inputCls} type="number" min={0} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={f.includesTax} onChange={(e) => setF({ ...f, includesTax: e.target.checked })} />
              El precio incluye IVA
            </label>
            <PlanTaxHint price={f.price} includesTax={f.includesTax} taxRate={Number(plan.taxRate ?? 16)} />
          </div>
          <div className="flex gap-3">
            <div className="flex-1"><label className="text-[12px] text-slate-500">Vigencia (días)</label>
              <input className={inputCls} type="number" min={1} value={f.validityDays} onChange={(e) => setF({ ...f, validityDays: e.target.value })} /></div>
            <div className="flex-1"><label className="text-[12px] text-slate-500">Clases incluidas</label>
              <input className={inputCls} type="number" min={1} disabled={plan.planType !== 'PACKAGE_N'}
                     placeholder={plan.planType === 'UNLIMITED_MONTH' ? 'Ilimitadas' : ''}
                     value={plan.planType === 'PACKAGE_N' ? f.classesIncluded : (plan.classesIncluded ?? '')}
                     onChange={(e) => setF({ ...f, classesIncluded: e.target.value })} /></div>
          </div>
          {plan.planType !== 'SINGLE' && (
            <div className="flex items-end gap-3">
              <div className="flex-1"><label className="text-[12px] text-slate-500">Reservas por día</label>
                <input className={inputCls} type="number" min={0} disabled={f.noCap} value={f.noCap ? '' : f.bookingsPerDay}
                       onChange={(e) => setF({ ...f, bookingsPerDay: e.target.value })} /></div>
              <label className="mb-2 flex items-center gap-1.5 text-sm text-slate-600">
                <input type="checkbox" checked={f.noCap} onChange={(e) => setF({ ...f, noCap: e.target.checked })} /> sin tope
              </label>
            </div>
          )}
          <p className="rounded-xl bg-slate-50 px-3 py-2 text-[12px] text-slate-500">{EDIT_PLAN_NOTE}</p>
          {err && <p className="text-sm text-rose-600">{err}</p>}
        </div>
        <div className="shrink-0 flex justify-end gap-2 border-t border-slate-100 p-6 py-3">
          <button className="rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-50" onClick={onClose}>Cancelar</button>
          <button className={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §20.1 — Tipos de acceso (catálogo editable; etiqueta, NO agregadores)
// ---------------------------------------------------------------------------
function AccessTypesManager() {
  const [types, setTypes] = useState<AccessType[]>([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api<AccessType[]>('/gym/access-types').then(setTypes).catch(() => setTypes([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { setErr('Escribe un nombre'); return; }
    setSaving(true); setErr(null);
    try {
      await api('/gym/access-types', { method: 'POST', body: { name: name.trim() } });
      setName(''); load();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo crear');
    } finally { setSaving(false); }
  };

  const toggle = async (t: AccessType) => {
    await api(`/gym/access-types/${t.id}`, { method: 'PUT', body: { isActive: !t.isActive } });
    load();
  };

  return (
    <div className={`${card} p-5`}>
      <h3 className="mb-1 font-serif text-base text-slate-900">Tipos de acceso</h3>
      <p className="mb-3 text-[12px] text-slate-400">Etiqueta del check-in (interno, visita, cortesía…). No es integración con TotalPass/Wellhub.</p>
      <div className="flex gap-2">
        <input className={inputCls} placeholder="Nuevo tipo (p.ej. Visita)" value={name}
               onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
        <button className={btnPrimary} onClick={create} disabled={saving}>{saving ? '…' : 'Agregar'}</button>
      </div>
      {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
      <div className="mt-3 divide-y divide-slate-100">
        {types.length === 0 && <p className="py-3 text-sm text-slate-400">Sin tipos de acceso.</p>}
        {types.map((t) => (
          <div key={t.id} className="flex items-center justify-between py-2 text-sm">
            <span className={t.isActive ? 'text-slate-800' : 'text-slate-400'}>
              {t.name} {!t.isActive && <span className="text-[11px]">(inactivo)</span>}
            </span>
            <button className={btnGhost} onClick={() => toggle(t)}>{t.isActive ? 'Desactivar' : 'Activar'}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §20.1 — Reporte de visitas / asistencia (read-only)
// ---------------------------------------------------------------------------
type VisitsReport = {
  from: string; to: string;
  totals: { total: number; ok: number; blocked: number };
  byDay: Array<{ day: string; total: number; ok: number; blocked: number }>;
  byAccessType: Array<{ name: string; total: number; ok: number }>;
  byPlan: Array<{ name: string; total: number; ok: number }>;
};

export function GymVisitsView() {
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  const [data, setData] = useState<VisitsReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api(`/gym/insights/visits?${new URLSearchParams(periodToQuery(period)).toString()}`)
      .then((r) => { if (!cancelled) setData(r as VisitsReport); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Visitas / asistencia</h2>
          <p className="text-sm text-slate-500">Accesos por día, tipo y plan — {periodLabel(period)}.</p>
        </div>
        <DatePeriodFilter value={period} onChange={setPeriod} />
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : !data ? (
        <p className="text-sm text-slate-400">Sin datos para el período.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className={`${card} px-5 py-3`}><p className="text-[11px] uppercase tracking-wide text-slate-400">Visitas</p><p className="font-serif text-2xl tabular-nums text-slate-900">{data.totals.total}</p></div>
            <div className={`${card} px-5 py-3`}><p className="text-[11px] uppercase tracking-wide text-slate-400">Accesos OK</p><p className="font-serif text-2xl tabular-nums text-emerald-700">{data.totals.ok}</p></div>
            <div className={`${card} px-5 py-3`}><p className="text-[11px] uppercase tracking-wide text-slate-400">Bloqueos</p><p className="font-serif text-2xl tabular-nums text-rose-600">{data.totals.blocked}</p></div>
          </div>

          <div className={`${card} p-5`}>
            <h3 className="mb-3 font-serif text-lg text-slate-900">Asistencia por día</h3>
            {data.byDay.length === 0 ? <p className="text-sm text-slate-400">Sin visitas en el período.</p> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.byDay} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
                    tickFormatter={(d: string) => d.slice(8, 10)} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
                  <Tooltip formatter={(v: any, n: any) => [v, n === 'ok' ? 'OK' : n === 'blocked' ? 'Bloqueos' : 'Total']}
                    labelFormatter={(d) => `Día ${d}`} />
                  <Bar dataKey="ok" stackId="a" radius={[0, 0, 0, 0]}>
                    {data.byDay.map((_, i) => <Cell key={i} fill="#0f766e" />)}
                  </Bar>
                  <Bar dataKey="blocked" stackId="a" radius={[3, 3, 0, 0]}>
                    {data.byDay.map((_, i) => <Cell key={i} fill="#e11d48" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <VisitsBreakdown title="Por tipo de acceso" rows={data.byAccessType} />
            <VisitsBreakdown title="Por plan" rows={data.byPlan} />
          </div>
        </>
      )}

      {/* Analítica por miembro (operativo, sin financials). */}
      <RetentionPanel period={period} />
      <AtRiskPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Retención por plan — % activos vs vencidos + visita promedio (período).
// ---------------------------------------------------------------------------
type RetentionReport = {
  from: string; to: string;
  plans: Array<{ plan: string; active: number; expired: number; total: number; retentionPct: number; avgVisits: number }>;
};

function RetentionPanel({ period }: { period: PeriodValue }) {
  const [data, setData] = useState<RetentionReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api(`/gym/insights/retention?${new URLSearchParams(periodToQuery(period)).toString()}`)
      .then((r) => { if (!cancelled) setData(r as RetentionReport); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  return (
    <div className={`${card} p-5`}>
      <h3 className="mb-1 font-serif text-lg text-slate-900">Retención por plan</h3>
      <p className="mb-3 text-[12px] text-slate-400">Miembros activos vs vencidos (a hoy) y visita promedio por miembro activo en {periodLabel(period)}.</p>
      {loading ? <p className="text-sm text-slate-400">Cargando…</p> : !data || data.plans.length === 0 ? (
        <p className="text-sm text-slate-400">Sin miembros aún.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="py-1">Plan</th>
              <th className="py-1 text-right">Activos</th>
              <th className="py-1 text-right">Vencidos</th>
              <th className="py-1 text-right">Retención</th>
              <th className="py-1 text-right">Visita prom.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.plans.map((p) => (
              <tr key={p.plan}>
                <td className="py-1.5 text-slate-700">{p.plan}</td>
                <td className="py-1.5 text-right tabular-nums text-emerald-700">{p.active}</td>
                <td className="py-1.5 text-right tabular-nums text-rose-600">{p.expired}</td>
                <td className="py-1.5 text-right tabular-nums font-medium text-slate-800">{p.retentionPct}%</td>
                <td className="py-1.5 text-right tabular-nums text-slate-700">{p.avgVisits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Miembros en riesgo — activos sin check-in en los últimos N días (ajustable).
// Accionable: contactar/recordar (WhatsApp/email) reusando ContactActions.
// ---------------------------------------------------------------------------
type AtRiskReport = {
  days: number;
  members: Array<{
    subscriptionId: string; contactId: string; memberName: string;
    memberEmail: string | null; memberPhone: string | null;
    planName: string | null; endDate: string;
    lastVisit: string | null; daysSince: number | null;
  }>;
};

function AtRiskPanel() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState<AtRiskReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api(`/gym/insights/at-risk?days=${days}`)
      .then((r) => { if (!cancelled) setData(r as AtRiskReport); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className={`${card} p-5`}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-serif text-lg text-slate-900">Miembros en riesgo</h3>
        <label className="flex items-center gap-2 text-[12px] text-slate-500">
          Sin visita en
          <select className="rounded-lg border border-slate-200 px-2 py-1 text-sm"
            value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 14, 21, 30, 45, 60].map((d) => <option key={d} value={d}>{d} días</option>)}
          </select>
        </label>
      </div>
      <p className="mb-3 text-[12px] text-slate-400">Miembros vigentes (al corriente) que no asisten desde hace más de {days} días. Contáctalos antes de que se den de baja.</p>
      {loading ? <p className="text-sm text-slate-400">Cargando…</p> : !data || data.members.length === 0 ? (
        <p className="text-sm text-emerald-600">Sin miembros en riesgo. 🎉</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {data.members.map((m) => (
            <div key={m.subscriptionId} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{m.memberName || 'Miembro'}</p>
                <p className="text-[12px] text-slate-400">
                  {m.planName ?? '—'} · {m.lastVisit ? `última visita ${fmtDate(m.lastVisit)}${m.daysSince != null ? ` (hace ${m.daysSince} días)` : ''}` : 'nunca ha asistido'}
                </p>
              </div>
              <ContactActions name={m.memberName} phone={m.memberPhone} email={m.memberEmail}
                message="te extrañamos en el estudio, ¿retomamos tu rutina?" subject="Te extrañamos" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisitsBreakdown({ title, rows }: { title: string; rows: Array<{ name: string; total: number; ok: number }> }) {
  return (
    <div className={`${card} p-5`}>
      <h3 className="mb-3 font-serif text-lg text-slate-900">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-slate-400">Sin datos.</p> : (
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-slate-400">
            <tr><th className="py-1">Nombre</th><th className="py-1 text-right">OK</th><th className="py-1 text-right">Total</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.name}>
                <td className="py-1.5 text-slate-700">{r.name}</td>
                <td className="py-1.5 text-right tabular-nums text-emerald-700">{r.ok}</td>
                <td className="py-1.5 text-right tabular-nums font-medium text-slate-800">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
